class LocalCDP {
  constructor(options = {}) {
    this.storageKey = options.storageKey || 'cdpLayer';
    this.sessionTimeoutMinutes = options.sessionTimeoutMinutes || 30;
    this.maxEvents = options.maxEvents || 500;

    this.existingHiddenFieldName = options.existingHiddenFieldName || 'cdp';

    this.formPayloadMaxEvents = options.formPayloadMaxEvents || 80;
    this.formPayloadMaxChars = options.formPayloadMaxChars || 45000;

    this.dedupTtlPageViewSeconds = options.dedupTtlPageViewSeconds || 10;
    this.dedupTtlVehicleViewSeconds = options.dedupTtlVehicleViewSeconds || 300;

    this.stripQueryParams = options.stripQueryParams || ['_token', 'gclid', 'fbclid', 'msclkid', 'yclid'];

    // NEW: auto-detect “pagina veicolo”
    this.autoTrackVehicle = options.autoTrackVehicle !== false;
    this.vehicleSelectors = options.vehicleSelectors || {
      stock_id: ['input[name="stock_id"]', 'input[name="id"]', '[data-stock-id]'],
      brand: ['input[name="brand"]', '[data-brand]'],
      modello: ['input[name="modello"]', '[data-model]'],
      prezzo_int: ['input[name="prezzo_int"]', '[data-price]'],
      canonical: ['input[name="canonical"]', 'link[rel="canonical"]'],
    };

    if (window.__cdp_initialized && window.cdp) return window.cdp;
    window.__cdp_initialized = true;

    this.layer = this._load() || this._init();
    this._ensureVisitor();
    this._ensureSession();
    this._captureFirstTouch();
    this._trackPageView();

    // NEW: se siamo su una scheda veicolo, traccia view_vehicle subito
    if (this.autoTrackVehicle) this._autoTrackVehicleOnPage();

    this._store();

    this.bindSchedaFormsAuto();

    window.cdp = this;
  }

  _init() {
    return {
      meta: {
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        host: location.host,
        version: 'cdp-krayin-attrs-3',
      },
      visitor_id: null,
      session: null,
      events: [],
      last_vehicle: null, // SOLO da view_vehicle reale
      touches: { first: null, session: null },
      dedup: { keys: {} },
    };
  }

  _load() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  _store() {
    this.layer.meta.updated_at = new Date().toISOString();
    try { localStorage.setItem(this.storageKey, JSON.stringify(this.layer)); } catch {}
  }

  _setCookie(name, value, maxAgeSeconds = null) {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    const maxAge = maxAgeSeconds ? `; Max-Age=${maxAgeSeconds}` : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${maxAge}${secure}`;
  }

  _uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();

    if (window.crypto && window.crypto.getRandomValues) {
      const buf = new Uint8Array(16);
      window.crypto.getRandomValues(buf);
      buf[6] = (buf[6] & 0x0f) | 0x40;
      buf[8] = (buf[8] & 0x3f) | 0x80;
      const hex = Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  _ensureVisitor() {
    if (!this.layer.visitor_id) {
      this.layer.visitor_id = this._uuid();
      this._setCookie('visitor_id', this.layer.visitor_id, 180 * 24 * 60 * 60);
    }
  }

  _ensureSession() {
    const now = Date.now();
    const timeout = this.sessionTimeoutMinutes * 60 * 1000;

    const last = this.layer.session && this.layer.session.last_activity
      ? new Date(this.layer.session.last_activity).getTime()
      : null;

    if (!this.layer.session || !last || (now - last > timeout)) {
      this.layer.session = {
        session_id: this._uuid(),
        started_at: new Date().toISOString(),
        last_activity: new Date().toISOString(),
      };
      this._setCookie('session_id', this.layer.session.session_id);
      this._captureSessionTouch();
    } else {
      this.layer.session.last_activity = new Date().toISOString();
    }
  }

  _stripUrl(url) {
    if (!url) return url;
    try {
      const u = new URL(url, location.origin);
      for (let i = 0; i < this.stripQueryParams.length; i++) u.searchParams.delete(this.stripQueryParams[i]);
      return u.toString();
    } catch {
      return url;
    }
  }

  _toPath(url) {
    if (!url) return null;
    try {
      const u = new URL(url, location.origin);
      return u.pathname || null;
    } catch {
      return null;
    }
  }

  _getCurrentUTM() {
    const sp = new URLSearchParams(location.search);
    const utm = {
      utm_source: sp.get('utm_source'),
      utm_medium: sp.get('utm_medium'),
      utm_campaign: sp.get('utm_campaign'),
      utm_term: sp.get('utm_term'),
      utm_content: sp.get('utm_content'),
    };
    const hasAny = Object.values(utm).some(v => v && String(v).trim() !== '');
    return hasAny ? utm : null;
  }

  _captureFirstTouch() {
    if (!this.layer.touches) this.layer.touches = { first: null, session: null };
    if (!this.layer.touches.first) {
      const utm = this._getCurrentUTM();
      if (utm) {
        this.layer.touches.first = Object.assign({}, utm, {
          captured_at: new Date().toISOString(),
          landing_url: this._stripUrl(location.href),
        });
      }
    }
  }

  _captureSessionTouch() {
    if (!this.layer.touches) this.layer.touches = { first: null, session: null };
    const utm = this._getCurrentUTM();
    this.layer.touches.session = Object.assign({}, (utm || {}), {
      captured_at: new Date().toISOString(),
      landing_url: this._stripUrl(location.href),
    });
  }

  _dedup(key, ttlSeconds) {
    const map = this.layer.dedup && this.layer.dedup.keys ? this.layer.dedup.keys : {};
    const lastIso = map[key];
    const now = Date.now();

    if (lastIso) {
      const last = new Date(lastIso).getTime();
      if (now - last < ttlSeconds * 1000) return false;
    }

    map[key] = new Date().toISOString();
    if (!this.layer.dedup) this.layer.dedup = { keys: {} };
    this.layer.dedup.keys = map;
    return true;
  }

  _baseEvent(type) {
    this._captureFirstTouch();

    const current = this._getCurrentUTM();
    const sessionTouch = this.layer.touches && this.layer.touches.session ? this.layer.touches.session : {};
    const firstTouch = this.layer.touches && this.layer.touches.first ? this.layer.touches.first : {};

    const utm_source = (current && current.utm_source) || sessionTouch.utm_source || firstTouch.utm_source || null;
    const utm_medium = (current && current.utm_medium) || sessionTouch.utm_medium || firstTouch.utm_medium || null;
    const utm_campaign = (current && current.utm_campaign) || sessionTouch.utm_campaign || firstTouch.utm_campaign || null;

    return {
      type,
      created_at: new Date().toISOString(),
      visitor_id: this.layer.visitor_id,
      session_id: this.layer.session ? this.layer.session.session_id : null,
      url: this._stripUrl(location.origin + location.pathname),
      title: document.title,
      referrer: this._stripUrl(document.referrer || null),
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term: (current && current.utm_term) || sessionTouch.utm_term || firstTouch.utm_term || null,
      utm_content: (current && current.utm_content) || sessionTouch.utm_content || firstTouch.utm_content || null,
    };
  }

  _pushEvent(event) {
    if (event && event.url) event.url = this._stripUrl(event.url);
    if (event && event.referrer) event.referrer = this._stripUrl(event.referrer);
    if (event && event.canonical) event.canonical = this._stripUrl(event.canonical);

    this.layer.events.push(event);

    if (this.layer.events.length > this.maxEvents) this.layer.events = this.layer.events.slice(-this.maxEvents);
    if (this.layer.session) this.layer.session.last_activity = new Date().toISOString();
  }

  _trackPageView() {
    const key = `page_view:${location.origin + location.pathname}`;
    if (!this._dedup(key, this.dedupTtlPageViewSeconds)) return;
    this._pushEvent(this._baseEvent('page_view'));
  }

  addEngagement(data = {}) { this._pushEvent(Object.assign(this._baseEvent('engagement'), data)); this._store(); }
  addClick(data = {}) { this._pushEvent(Object.assign(this._baseEvent('click'), data)); this._store(); }
  addSearch(data = {}) { this._pushEvent(Object.assign(this._baseEvent('search'), data)); this._store(); }

  // ---------------- AUTO VEHICLE DETECT ----------------

  _qsFirst(selectors) {
    for (let i = 0; i < selectors.length; i++) {
      const el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }

  _readFromEl(el, key) {
    if (!el) return null;
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
      return el.value != null ? String(el.value).trim() : null;
    }
    const dsKey = (key || '').replace(/_([a-z])/g, (_, c) => c.toUpperCase()); // stock_id -> stockId
    if (el.dataset && el.dataset[dsKey] != null) return String(el.dataset[dsKey]).trim();
    if (el.getAttribute) {
      const v = el.getAttribute('content') || el.getAttribute('href') || el.getAttribute('data-' + key);
      return v != null ? String(v).trim() : null;
    }
    return null;
  }

  _autoTrackVehicleOnPage() {
    // se non c'è stock_id, non è una scheda veicolo (o almeno non la riconosciamo)
    const elStock = this._qsFirst(this.vehicleSelectors.stock_id || []);
    const stock_id = this._readFromEl(elStock, 'stock_id');
    if (!stock_id) return;

    const elBrand = this._qsFirst(this.vehicleSelectors.brand || []);
    const elModel = this._qsFirst(this.vehicleSelectors.modello || []);
    const elPrice = this._qsFirst(this.vehicleSelectors.prezzo_int || []);
    const elCan = this._qsFirst(this.vehicleSelectors.canonical || []);

    const canonical =
      this._readFromEl(elCan, 'canonical') ||
      (document.querySelector('link[rel="canonical"]') ? document.querySelector('link[rel="canonical"]').href : null) ||
      (location.origin + location.pathname);

    this.trackVehicleView({
      stock_id,
      brand: this._readFromEl(elBrand, 'brand'),
      modello: this._readFromEl(elModel, 'modello'),
      prezzo_int: this._readFromEl(elPrice, 'prezzo_int'),
      canonical,
    });
  }

  // ---------------- VEHICLE VIEW ----------------

  trackVehicleView(vehicle = {}) {
    const canonical = vehicle.canonical || (location.origin + location.pathname);
    const key = `view_vehicle:${canonical}`;
    if (!this._dedup(key, this.dedupTtlVehicleViewSeconds)) return;

    const payload = Object.assign(this._baseEvent('view_vehicle'), {
      canonical: this._stripUrl(canonical),
      stock_id: vehicle.stock_id || null,
      brand: vehicle.brand || null,
      modello: vehicle.modello || null,
      prezzo_int: vehicle.prezzo_int || null,
    });

    this.layer.last_vehicle = {
      canonical: payload.canonical,
      stock_id: payload.stock_id,
      brand: payload.brand,
      modello: payload.modello,
      prezzo_int: payload.prezzo_int,
      viewed_at: payload.created_at,
    };

    this._pushEvent(payload);
    this._store();
  }

  // ---------------- ATTRIBUTI / PAYLOAD ----------------

  _eventsForCurrentSession() {
    const sid = this.layer.session ? this.layer.session.session_id : null;
    if (!sid) return [];
    const allowed = { page_view: 1, view_vehicle: 1, search: 1, click: 1, engagement: 1, lead_submit: 1 };

    return (this.layer.events || [])
      .filter(e => e && e.session_id === sid && allowed[e.type])
      .slice(-this.formPayloadMaxEvents);
  }

  _getPrevPaths(events, excludePath, limit = 5) {
    const out = [];
    const seen = {};
    let last = null;

    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (!e || e.type !== 'page_view' || !e.url) continue;

      const path = this._toPath(e.url);
      if (!path) continue;

      if (excludePath && path === excludePath) continue;
      if (last === path) continue;
      last = path;

      if (seen[path]) continue;
      seen[path] = 1;

      out.push(path);
      if (out.length >= limit) break;
    }
    return out;
  }

  _countByType(events) {
    const counts = { page_view: 0, view_vehicle: 0 };
    for (let i = 0; i < events.length; i++) {
      const t = events[i] && events[i].type;
      if (t === 'page_view') counts.page_view++;
      if (t === 'view_vehicle') counts.view_vehicle++;
    }
    return counts;
  }

  _findLastVehicleBeforeLead(events, leadCanonicalOrUrl) {
    const leadPath = this._toPath(leadCanonicalOrUrl || location.href);

    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (!e || e.type !== 'view_vehicle') continue;

      const can = e.canonical || e.url;
      const p = this._toPath(can);

      if (leadPath && p && p === leadPath) continue;

      return {
        canonical: can ? this._stripUrl(can) : null,
        stock_id: e.stock_id || null,
        brand: e.brand || null,
        modello: e.modello || null,
        prezzo_int: e.prezzo_int || null,
        viewed_at: e.created_at || null,
      };
    }
    return null;
  }

  _buildKrayinAttributes(leadCanonical = null) {
    const events = this._eventsForCurrentSession();
    const counts = this._countByType(events);

    const excludePath = this._toPath(leadCanonical || location.href);
    const prev = this._getPrevPaths(events, excludePath, 5);

    const first = this.layer.touches && this.layer.touches.first ? this.layer.touches.first : null;
    const lastBeforeLead = this._findLastVehicleBeforeLead(events, leadCanonical || location.href);

    return {
      cdp_visitor_id: this.layer.visitor_id || null,
      cdp_first_utm_source: first && first.utm_source ? first.utm_source : null,
      cdp_first_utm_medium: first && first.utm_medium ? first.utm_medium : null,
      cdp_first_utm_campaign: first && first.utm_campaign ? first.utm_campaign : null,

      cdp_prev_url_1: prev[0] || null,
      cdp_prev_url_2: prev[1] || null,
      cdp_prev_url_3: prev[2] || null,
      cdp_prev_url_4: prev[3] || null,
      cdp_prev_url_5: prev[4] || null,

      cdp_last_vehicle_stock_id: lastBeforeLead ? lastBeforeLead.stock_id : null,
      cdp_last_vehicle_brand: lastBeforeLead ? lastBeforeLead.brand : null,
      cdp_last_vehicle_model: lastBeforeLead ? lastBeforeLead.modello : null,
      cdp_last_vehicle_price: lastBeforeLead ? lastBeforeLead.prezzo_int : null,

      cdp_views_count: String(counts.page_view),
      cdp_vehicle_views_count: String(counts.view_vehicle),
    };
  }

  _buildCdpPayload(leadCanonical = null) {
    const events = this._eventsForCurrentSession();

    const payload = {
      meta: {
        host: this.layer.meta && this.layer.meta.host ? this.layer.meta.host : null,
        version: this.layer.meta && this.layer.meta.version ? this.layer.meta.version : null,
        sent_at: new Date().toISOString(),
      },
      visitor_id: this.layer.visitor_id || null,
      session: this.layer.session ? {
        session_id: this.layer.session.session_id,
        started_at: this.layer.session.started_at,
        last_activity: this.layer.session.last_activity,
      } : null,
      touches: this.layer.touches || null,
      last_vehicle: this.layer.last_vehicle || null,
      attributes: this._buildKrayinAttributes(leadCanonical),
      events,
    };

    let json = JSON.stringify(payload);

    if (json.length > this.formPayloadMaxChars) {
      payload.events = payload.events.slice(-30);
      payload.attributes = this._buildKrayinAttributes(leadCanonical);
      json = JSON.stringify(payload);

      if (json.length > this.formPayloadMaxChars) {
        const minimal = {
          meta: payload.meta,
          visitor_id: payload.visitor_id,
          session: payload.session,
          attributes: payload.attributes,
        };
        json = JSON.stringify(minimal);
      }
    }

    return json;
  }

  _fillHiddenCdpIfPresent(form, leadCanonical = null) {
    if (!form) return false;
    const name = this.existingHiddenFieldName;
    if (!name) return false;

    const hidden = form.querySelector(`input[name="${CSS.escape(name)}"]`);
    if (!hidden) return false;

    hidden.value = this._buildCdpPayload(leadCanonical);
    return true;
  }

  _isSchedaForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    return !!form.querySelector('input[name="form_type"][value="scheda"]');
  }

  _bindOneSchedaForm(form) {
    if (!this._isSchedaForm(form)) return;
    if (form.__cdpBound) return;
    form.__cdpBound = true;

    const read = (name) => {
      const el = form.querySelector(`[name="${CSS.escape(name)}"]`);
      return el && el.value != null ? String(el.value).trim() : null;
    };

    const leadCanonical =
      read('vehicle_url') ||
      read('canonical') ||
      (location.origin + location.pathname);

    // Popola subito quando appare (modal)
    this._fillHiddenCdpIfPresent(form, leadCanonical);

    const refresh = () => {
      const sid = this.layer.session && this.layer.session.session_id ? this.layer.session.session_id : '';
      const key = `lead_submit:${leadCanonical}:${sid}`;

      if (this._dedup(key, 30)) {
        this._pushEvent(Object.assign(this._baseEvent('lead_submit'), {
          canonical: this._stripUrl(leadCanonical),
        }));
      }

      this._fillHiddenCdpIfPresent(form, leadCanonical);
      this._store();
    };

    form.addEventListener('submit', refresh, true);

    form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(el => {
      el.addEventListener('click', refresh, true);
      el.addEventListener('mousedown', refresh, true);
      el.addEventListener('keydown', (e) => { if (e.key === 'Enter') refresh(); }, true);
    });
  }

  bindSchedaFormsAuto() {
    if (this._autoBindStarted) return;
    this._autoBindStarted = true;

    const scan = () => {
      const forms = document.querySelectorAll('form');
      for (let i = 0; i < forms.length; i++) this._bindOneSchedaForm(forms[i]);
    };

    scan();
    new MutationObserver(() => scan()).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shown.bs.modal', () => scan(), true);
  }

  getLayer() { return this.layer; }

  clear() {
    try { localStorage.removeItem(this.storageKey); } catch {}
    this.layer = this._init();
    this._ensureVisitor();
    this._ensureSession();
    this._captureFirstTouch();
    this._trackPageView();
    if (this.autoTrackVehicle) this._autoTrackVehicleOnPage();
    this._store();
  }
}

// -------- INIT --------
const cdp = new LocalCDP({
  storageKey: 'cdpLayer',
  sessionTimeoutMinutes: 30,
  maxEvents: 500,
  existingHiddenFieldName: 'cdp',
  formPayloadMaxEvents: 80,
  formPayloadMaxChars: 45000,
  stripQueryParams: ['_token', 'gclid', 'fbclid', 'msclkid', 'yclid'],
  autoTrackVehicle: true,
});